import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PMDialog } from '../../utils/PMDialog';
import { trainingApi } from '../../../api/training';
import { normalizeError } from '../../../operationFeedback/normalizeError';
import { buttonClass, Feedback } from './trainingAdminShared';

export default function TrainingDependencyIssues({ guide, onClose, onDeactivate, onEdit, onChecked }) {
  const { t } = useTranslation();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const sequence = useRef(0);
  const checked = useRef(onChecked);
  checked.current = onChecked;
  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true); setError(null); setResult(null);
    try {
      const response = await trainingApi.dependencyIssues(guide.id, { offset, limit: 20 });
      if (request !== sequence.current) return;
      setResult(response);
      checked.current?.(guide.id, response.count);
    } catch (failure) {
      if (request === sequence.current) setError(normalizeError(failure));
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }, [guide.id, offset]);
  useEffect(() => { load(); return () => { sequence.current += 1; }; }, [load]);
  return <PMDialog open title={t('trainingAdmin.healthTitle')} onClose={onClose} closeOnOutsideClick panelClassName="sm:!max-w-3xl">
    <div className="min-h-0 space-y-4 overflow-auto p-1">
      <h2 className="text-lg font-bold text-slate-900">{guide.title}</h2>
      <p className="text-sm leading-6 text-slate-600">{t('trainingAdmin.healthHelp')}</p>
      <Feedback error={error} />
      {loading && <p role="status">{t('trainingAdmin.healthChecking')}</p>}
      {result && (result.count === 0 ? <p role="status" className="rounded-xl bg-green-50 p-4 text-green-800">{t('trainingAdmin.healthClear')}</p> : <>
        <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 font-semibold text-rose-800">{t('trainingAdmin.healthBlocked', { count: result.count })}</p>
        <div className="overflow-x-auto rounded-xl border border-slate-200"><table className="w-full text-left text-sm">
          <thead className="bg-slate-50 text-slate-600"><tr>{['user', 'card', 'healthProblem'].map((key) => <th className="p-3" key={key}>{t(`trainingAdmin.${key}`)}</th>)}</tr></thead>
          <tbody>{result.data.map((row) => row.issues.map((issue, index) => <tr key={`${row.assignment_id}:${index}`} className="border-t border-slate-100">
            <td className="p-3 align-top font-semibold">{row.user.title}<div className="text-xs font-normal text-slate-500">{t('trainingAdmin.revision')} {row.revision_number}</div></td>
            <td className="p-3 align-top">{issue.card_title}{issue.document_title && <p className="mt-1 text-xs text-slate-500">{issue.document_title}</p>}</td>
            <td className="p-3 align-top text-rose-800">{t(issue.kind === 'image' ? 'trainingAdmin.healthImage' : 'trainingAdmin.healthDocument')}</td>
          </tr>))}</tbody>
        </table></div>
      </>)}
      {result && result.count > 20 && <div className="flex justify-between gap-2">
        <button className={buttonClass} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - 20))}>{t('trainingAdmin.previous')}</button>
        <span className="self-center text-sm">{offset + 1}–{Math.min(offset + 20, result.count)} / {result.count}</span>
        <button className={buttonClass} disabled={offset + 20 >= result.count} onClick={() => setOffset(offset + 20)}>{t('trainingAdmin.next')}</button>
      </div>}
      <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
        <button className={buttonClass} disabled={loading} onClick={load}>{t('trainingAdmin.healthRecheck')}</button>
        <button className={buttonClass} onClick={onEdit}>{t(guide.draft_revision ? 'trainingAdmin.edit' : 'trainingAdmin.revise')}</button>
        <button className={`${buttonClass} !text-rose-700`} onClick={onDeactivate}>{t('trainingAdmin.deactivate')}</button>
        <button className={buttonClass} onClick={onClose}>{t('trainingAdmin.close')}</button>
      </div>
    </div>
  </PMDialog>;
}
